import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../components/UI/Button';
import Card from '../components/UI/Card';
import BookingPriceSummary from '../components/Booking/BookingPriceSummary';
import AvailabilityDatePicker from '../components/Booking/AvailabilityDatePicker';
import GuestSelector, { DEFAULT_GUESTS, getTravelersCount } from '../components/Booking/GuestSelector';
import { formatMad } from '../utils/currency';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import useScrollToTop from '../hooks/useScrollToTop';

const BookingScreen = () => {
  // Scroll to top when component mounts
  useScrollToTop();
  const { user, isAuthenticated, login, register } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    villa: '',
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    guests: {
      adults: parseInt(searchParams.get('adults')) || parseInt(searchParams.get('guests')) || DEFAULT_GUESTS.adults,
      children: parseInt(searchParams.get('children')) || DEFAULT_GUESTS.children,
      babies: parseInt(searchParams.get('babies')) || DEFAULT_GUESTS.babies,
      pets: parseInt(searchParams.get('pets')) || DEFAULT_GUESTS.pets,
    },
    services: [],
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      specialRequests: ''
    }
  });
  const [totalPrice, setTotalPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [villas, setVillas] = useState([]);
  const [villasLoading, setVillasLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('guest'); // 'guest', 'login', 'register'
  const [authData, setAuthData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: ''
  });

  const villasWithLabels = useMemo(() => {
    return villas.map((villa) => ({
      ...villa,
      name:
        villa.id === 'najat'
          ? t('booking.villas.najat.name')
          : t('booking.villas.nadia.name'),
      features:
        villa.id === 'najat'
          ? t('booking.villas.najat.features', { returnObjects: true })
          : t('booking.villas.nadia.features', { returnObjects: true }),
    }));
  }, [villas, t]);

  const selectedVilla = villasWithLabels.find((v) => v.id === bookingData.villa);
  const travelersCount = getTravelersCount(bookingData.guests);
  const maxTravelers = selectedVilla?.max_guests || selectedVilla?.maxGuests || 8;
  const guestsValid = bookingData.guests.adults >= 1 && travelersCount <= maxTravelers;

  const additionalServices = [
    { id: 'halfboard', name: 'Half Board', price: 200, description: 'Per person per day' },
    { id: 'fullboard', name: 'Full Board', price: 300, description: 'Per person per day' }
  ];

  const steps = [
    { number: 1, title: t('booking.step1'), description: t('booking.selectVilla') },
    { number: 2, title: t('booking.step2'), description: t('booking.guestInfo') },
    { number: 3, title: t('booking.step3'), description: t('booking.additionalServices') },
    { number: 4, title: t('booking.step4'), description: t('booking.bookingSummary') },
    { number: 5, title: t('booking.confirmBooking'), description: t('booking.processing') }
  ];

  const calculateServicesTotal = () => {
    return bookingData.services.reduce((sum, serviceId) => {
      const service = additionalServices.find(s => s.id === serviceId);
      return sum + (service ? service.price : 0);
    }, 0);
  };

  const calculatePrice = useCallback(() => {
    const base = quote?.available ? Number(quote.total_price || 0) : 0;
    return base + calculateServicesTotal();
  }, [quote, bookingData.services]);

  useEffect(() => {
    const loadVillas = async () => {
      try {
        const response = await api.getSmoobuVillas();
        setVillas(response.villas || []);
      } catch (error) {
        showError(error.message || 'Unable to load villas');
        setVillas([
          {
            id: 'najat',
            name: t('booking.villas.najat.name'),
            max_guests: 6,
            image: '/assets/images/villa Baidane-15.jpg',
            available: true,
          },
          {
            id: 'nadia',
            name: t('booking.villas.nadia.name'),
            max_guests: 8,
            image: '/assets/images/villa-nadia/villa-nadia-00001.webp',
            available: false,
            available_date: t('booking.villas.nadia.availableDate'),
          },
        ]);
      } finally {
        setVillasLoading(false);
      }
    };

    loadVillas();
  }, [showError, t]);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!bookingData.villa || !bookingData.checkIn || !bookingData.checkOut) {
        setQuote(null);
        setQuoteError('');
        return;
      }

      const checkIn = new Date(bookingData.checkIn);
      const checkOut = new Date(bookingData.checkOut);
      if (checkOut <= checkIn) {
        setQuote(null);
        setQuoteError(t('booking.invalidDates'));
        return;
      }

      setQuoteLoading(true);
      setQuoteError('');

      try {
        const response = await api.getSmoobuQuote({
          villa: bookingData.villa,
          checkIn: bookingData.checkIn,
          checkOut: bookingData.checkOut,
          adults: bookingData.guests.adults,
          children: bookingData.guests.children,
          babies: bookingData.guests.babies,
          pets: bookingData.guests.pets,
        });
        setQuote(response.quote);
        if (!response.quote?.available) {
          setQuoteError(response.quote?.error || t('booking.datesUnavailable', 'These dates are not available.'));
        }
      } catch (error) {
        setQuote(null);
        setQuoteError(error.message || t('booking.quoteError', 'Unable to fetch live pricing.'));
      } finally {
        setQuoteLoading(false);
      }
    };

    const timer = setTimeout(fetchQuote, 350);
    return () => clearTimeout(timer);
  }, [bookingData.villa, bookingData.checkIn, bookingData.checkOut, bookingData.guests, t]);

  useEffect(() => {
    setTotalPrice(calculatePrice());
  }, [calculatePrice]);

  // Handle URL parameters on component mount (only run once)
  useEffect(() => {
    const checkIn = searchParams.get('checkIn');
    const checkOut = searchParams.get('checkOut');
    const guests = searchParams.get('guests');

    // if (checkIn || checkOut || guests) {
    //   showSuccess(t('booking.prefilledFromSearch'));
    // }
  }, []); 

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getPersonalInfoValue = (field) => {
    if (isAuthenticated) {
      switch (field) {
        case 'firstName': return user?.first_name || '';
        case 'lastName': return user?.last_name || '';
        case 'email': return user?.email || '';
        case 'phone': return user?.phone || '';
        default: return '';
      }
    }
    return bookingData.personalInfo[field] || '';
  };

  const handleLogin = async () => {
    if (!authData.email || !authData.password) {
      showError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      const result = await login(authData.email, authData.password);
      if (result.success) {
        // Auto-fill personal info from user profile
        setBookingData({
          ...bookingData,
          personalInfo: {
            ...bookingData.personalInfo,
            firstName: result.user.first_name || '',
            lastName: result.user.last_name || '',
            email: result.user.email || '',
            phone: result.user.phone || ''
          }
        });
        setAuthMode('guest'); // Switch to guest view to show filled form
      } else {
        showError(`Login failed: ${result.error}`);
      }
    } catch (error) {
      showError(`Login failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!authData.firstName || !authData.lastName || !authData.email || !authData.phone || !authData.password) {
      showError('Please fill in all fields');
      return;
    }

    if (authData.password !== authData.confirmPassword) {
      showError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      const result = await register({
        first_name: authData.firstName,
        last_name: authData.lastName,
        email: authData.email,
        phone: authData.phone,
        password: authData.password,
        password_confirm: authData.confirmPassword
      });

      if (result.success) {
        // Show success message
        showSuccess('Account created successfully! You are now logged in and your information has been filled automatically.');

        // Auto-fill personal info from registration
        setBookingData({
          ...bookingData,
          personalInfo: {
            ...bookingData.personalInfo,
            firstName: authData.firstName,
            lastName: authData.lastName,
            email: authData.email,
            phone: authData.phone
          }
        });
        setAuthMode('guest'); // Switch to guest view to show filled form

        // Scroll to top to show success
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Use the improved error message from API
        showError(result.error || 'Registration failed. Please try again.');

        // Scroll to top to show error
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      showError(`Registration failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      // Prepare booking data for API
      const bookingPayload = {
        villa: bookingData.villa,
        check_in: bookingData.checkIn,
        check_out: bookingData.checkOut,
        adults: bookingData.guests.adults,
        children: bookingData.guests.children,
        babies: bookingData.guests.babies,
        pets: bookingData.guests.pets,
        guests: travelersCount,
        first_name: getPersonalInfoValue('firstName'),
        last_name: getPersonalInfoValue('lastName'),
        email: getPersonalInfoValue('email'),
        phone: getPersonalInfoValue('phone'),
        special_requests: bookingData.personalInfo.specialRequests,
        services_total: calculateServicesTotal(),
        selected_services: bookingData.services.map(serviceId => {
          const service = additionalServices.find(s => s.id === serviceId);
          return {
            id: service.id,
            name: service.name,
            price: service.price,
            quantity: 1
          };
        })
      };

      console.log('Submitting booking:', bookingPayload);

      // Submit to backend
      const response = await api.createBooking(bookingPayload);

      setIsLoading(false);

      // Redirect to success page with booking data
      navigate('/booking-success', {
        state: {
          booking: response.booking
        }
      });

    } catch (error) {
      setIsLoading(false);
      console.error('Booking submission error:', error);
      const smoobuMessage = error.data?.smoobu;
      const detailMessage = error.data?.details ? JSON.stringify(error.data.details) : '';
      showError(
        smoobuMessage
          ? `${smoobuMessage}${detailMessage ? ` (${detailMessage})` : ''}`
          : `Booking submission failed: ${error.message}. Please try again or contact us directly.`
      );
    }
  };

  const StepIndicator = () => (
    <div className="mb-8 md:mb-12">
      {/* Mobile Step Indicator */}
      <div className="block md:hidden">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-sofia text-gray-500">
            Step {currentStep} of {steps.length}
          </div>
          <div className="text-sm font-sofia font-medium text-[#81736d]">
            {steps[currentStep - 1]?.title}
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-[#81736d] h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Desktop Step Indicator */}
      <div className="hidden md:flex items-center justify-center">
        {steps.map((step, index) => (
          <div key={step.number} className="flex items-center">
            <div className={`flex flex-col items-center ${index < steps.length - 1 ? 'mr-6 lg:mr-8' : ''}`}>
              <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center font-sofia font-bold transition-all duration-300 ${
                currentStep >= step.number
                  ? 'bg-[#81736d] text-white shadow-glow'
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {currentStep > step.number ? (
                  <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step.number
                )}
              </div>
              <div className="text-center mt-2">
                <div className={`font-sofia font-medium text-xs lg:text-sm ${
                  currentStep >= step.number ? 'text-[#81736d]' : 'text-gray-500'
                }`}>
                  {step.title}
                </div>
                <div className="font-sofia text-xs text-gray-400 hidden lg:block">
                  {step.description}
                </div>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div className={`h-0.5 w-12 lg:w-16 mx-2 lg:mx-4 transition-all duration-300 ${
                currentStep > step.number ? 'bg-[#81736d]' : 'bg-gray-200'
              }`}></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen  ">
      {/* Hero Section */}
      <section className="relative h-48 sm:h-56 md:h-72 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0  bg-black bg-opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat mix-blend-overlay"
          style={{
            backgroundImage: `url('/assets/images/villa Baidane-01.jpg')`
          }}
        ></div>

        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-sofia font-bold mb-2 md:mb-4 animate-fade-in-up">
            {t('booking.title')}
          </h1>
          <p className="text-lg md:text-xl font-sofia font-light animate-fade-in-up opacity-90" style={{animationDelay: '0.2s'}}>
            {t('booking.subtitle')}
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-8 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StepIndicator />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
            {/* Price summary first on mobile, sidebar on desktop */}
            <div className="order-1 lg:order-2 lg:col-span-1">
              <BookingPriceSummary
                selectedVilla={selectedVilla}
                bookingData={bookingData}
                quote={quote}
                quoteLoading={quoteLoading}
                quoteError={quoteError}
                servicesTotal={calculateServicesTotal()}
                totalPrice={totalPrice}
                compact={currentStep === 1 && !bookingData.villa}
              />
            </div>

            <div className="order-2 lg:order-1 lg:col-span-2">
          <Card className="animate-fade-in-up" padding="sm">
            {/* Step 1: Villa Selection */}
            {currentStep === 1 && (
              <div>
                <h2 className="text-xl sm:text-2xl font-sofia font-bold text-black mb-4 sm:mb-6">{t('booking.chooseVilla')}</h2>
                {villasLoading ? (
                  <div className="text-center py-12 font-sofia text-text-secondary">
                    {t('booking.loadingVillas', 'Loading villas...')}
                  </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {villasWithLabels.map((villa) => (
                    <div
                      key={villa.id}
                      className={`relative border-2 rounded-xl p-4 sm:p-6 cursor-pointer transition-all duration-300 ${
                        bookingData.villa === villa.id
                          ? 'border-[#81736d] bg-[#81736d]/5 shadow-glow'
                          : 'border-gray-200 hover:border-[#81736d]/50 hover:shadow-medium'
                      } ${!villa.available ? 'opacity-60 cursor-not-allowed' : ''}`}
                      onClick={() => villa.available && setBookingData({...bookingData, villa: villa.id})}
                    >
                      {!villa.available && (
                        <div className="absolute top-4 right-4 bg-gray-500 text-white px-3 py-1 rounded-full text-sm font-sofia">
                          {t('booking.availableFrom')} {villa.available_date || villa.availableDate}
                        </div>
                      )}
                      
                      <img 
                        src={villa.image} 
                        alt={villa.name}
                        className="w-full h-36 sm:h-48 object-cover rounded-lg mb-3 sm:mb-4"
                      />
                      
                      <h3 className="text-xl font-sofia font-bold text-black mb-2">{villa.name}</h3>
                      <p className="text-text-secondary font-sofia mb-4">
                        {t('booking.upToGuests', 'Up to')} {villa.max_guests || villa.maxGuests} {t('common.guests')}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {villa.features.map((feature, idx) => (
                          <span key={idx} className="text-xs font-sofia bg-[#81736d]/20 px-2 py-1 rounded-full">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}

            {/* Step 2: Dates & Guests */}
            {currentStep === 2 && (
              <div>
                <h2 className="text-2xl font-sofia font-bold text-black mb-6">{t('booking.step1')}</h2>
                <AvailabilityDatePicker
                  villa={bookingData.villa}
                  checkIn={bookingData.checkIn}
                  checkOut={bookingData.checkOut}
                  onChange={({ checkIn, checkOut }) =>
                    setBookingData({ ...bookingData, checkIn, checkOut })
                  }
                />

                <div className="mt-6">
                  <GuestSelector
                    value={bookingData.guests}
                    maxTravelers={maxTravelers}
                    onChange={(guests) => setBookingData({ ...bookingData, guests })}
                  />
                </div>

                {quoteError && (
                  <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm font-sofia text-red-700">
                    {quoteError}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Additional Services */}
            {currentStep === 3 && (
              <div>
                <h2 className="text-2xl font-sofia font-bold text-black mb-6">Additional Services</h2>
                <p className="text-text-secondary font-sofia mb-6">
                  Enhance your stay with our premium services (optional)
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {additionalServices.map((service) => (
                    <div
                      key={service.id}
                      className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                        bookingData.services.includes(service.id)
                          ? 'border-[#81736d] bg-[#81736d]/5 shadow-glow'
                          : 'border-gray-200 hover:border-[#81736d]/50 hover:shadow-medium'
                      }`}
                      onClick={() => {
                        let newServices;
                        const isBoard = service.id === 'halfboard' || service.id === 'fullboard';

                        if (bookingData.services.includes(service.id)) {
                          // Deselect current service
                          newServices = bookingData.services.filter(s => s !== service.id);
                        } else {
                          if (isBoard) {
                            // Ensure only one of Half Board or Full Board is selected
                            newServices = bookingData.services.filter(s => s !== 'halfboard' && s !== 'fullboard');
                            newServices.push(service.id);
                          } else {
                            newServices = [...bookingData.services, service.id];
                          }
                        }
                        setBookingData({...bookingData, services: newServices});
                      }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-sofia font-semibold text-black">{service.name}</h3>
                        <div className="text-lg font-sofia font-bold text-[#81736d]">
                          {formatMad(service.price)}
                        </div>
                      </div>
                      <p className="text-text-secondary font-sofia text-sm">
                        {service.description}
                      </p>
                      {bookingData.services.includes(service.id) && (
                        <div className="mt-2 text-[#81736d] font-sofia font-medium text-sm">
                          ✓ Selected
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {bookingData.services.length > 0 && (
                  <div className="mt-6 p-4 bg-accent/10 rounded-lg">
                    <h4 className="font-sofia font-semibold text-black mb-2">Selected Services:</h4>
                    <div className="space-y-1">
                      {bookingData.services.map(serviceId => {
                        const service = additionalServices.find(s => s.id === serviceId);
                        return (
                          <div key={serviceId} className="flex justify-between text-sm font-sofia">
                            <span>{service.name}</span>
                            <span>{formatMad(service.price)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="border-t border-accent/20 mt-2 pt-2 flex justify-between font-sofia font-semibold">
                      <span>Services Total:</span>
                      <span>{formatMad(calculateServicesTotal())}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Your Details */}
            {currentStep === 4 && (
              <div>
                <h2 className="text-2xl font-sofia font-bold text-black mb-6">Your Details</h2>

                {/* Authentication Status */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  {isAuthenticated ? (
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold">✓</span>
                      </div>
                      <div>
                        <p className="font-sofia font-semibold text-black">
                          Logged in as {user?.first_name} {user?.last_name}
                        </p>
                        <p className="text-sm text-text-secondary font-sofia">{user?.email}</p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="font-sofia font-semibold text-black mb-3">
                        Complete your booking as:
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={() => setAuthMode('guest')}
                          className={`px-4 py-2 rounded-lg font-sofia transition-all ${
                            authMode === 'guest'
                              ? 'bg-accent text-white'
                              : 'bg-white border border-gray-300 text-text-primary hover:border-accent'
                          }`}
                        >
                          {t('booking.guestCheckout')}
                        </button>
                        <button
                          onClick={() => setAuthMode('login')}
                          className={`px-4 py-2 rounded-lg font-sofia transition-all ${
                            authMode === 'login'
                              ? 'bg-accent text-white'
                              : 'bg-white border border-gray-300 text-text-primary hover:border-accent'
                          }`}
                        >
                          {t('booking.loginAccount')}
                        </button>
                        <button
                          onClick={() => setAuthMode('register')}
                          className={`px-4 py-2 rounded-lg font-sofia transition-all ${
                            authMode === 'register'
                              ? 'bg-accent text-white'
                              : 'bg-white border border-gray-300 text-text-primary hover:border-accent'
                          }`}
                        >
                          {t('booking.createAccount')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Guest Information Form */}
                {(authMode === 'guest' || isAuthenticated) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                        {t('booking.firstName')} *
                      </label>
                      <input
                        type="text"
                        value={isAuthenticated ? user?.first_name || '' : bookingData.personalInfo.firstName}
                        onChange={(e) => !isAuthenticated && setBookingData({
                          ...bookingData,
                          personalInfo: {...bookingData.personalInfo, firstName: e.target.value}
                        })}
                        disabled={isAuthenticated}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia disabled:bg-gray-100"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                        {t('booking.lastName')} *
                      </label>
                      <input
                        type="text"
                        value={isAuthenticated ? user?.last_name || '' : bookingData.personalInfo.lastName}
                        onChange={(e) => !isAuthenticated && setBookingData({
                          ...bookingData,
                          personalInfo: {...bookingData.personalInfo, lastName: e.target.value}
                        })}
                        disabled={isAuthenticated}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia disabled:bg-gray-100"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                        {t('booking.email')} *
                      </label>
                      <input
                        type="email"
                        value={isAuthenticated ? user?.email || '' : bookingData.personalInfo.email}
                        onChange={(e) => !isAuthenticated && setBookingData({
                          ...bookingData,
                          personalInfo: {...bookingData.personalInfo, email: e.target.value}
                        })}
                        disabled={isAuthenticated}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia disabled:bg-gray-100"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                        {t('booking.phone')} *
                      </label>
                      <input
                        type="tel"
                        value={isAuthenticated ? user?.phone || '' : bookingData.personalInfo.phone}
                        onChange={(e) => !isAuthenticated && setBookingData({
                          ...bookingData,
                          personalInfo: {...bookingData.personalInfo, phone: e.target.value}
                        })}
                        disabled={isAuthenticated}
                        placeholder="+212 6XX XXX XXX"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia disabled:bg-gray-100"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                        {t('booking.specialRequests')} ({t('common.optional')})
                      </label>
                      <textarea
                        value={bookingData.personalInfo.specialRequests}
                        onChange={(e) => setBookingData({
                          ...bookingData,
                          personalInfo: {...bookingData.personalInfo, specialRequests: e.target.value}
                        })}
                        rows="3"
                        placeholder="Any special requests or dietary requirements..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                      />
                    </div>
                  </div>
                )}

                {/* Login Form */}
                {authMode === 'login' && !isAuthenticated && (
                  <div className="max-w-md mx-auto">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={authData.email}
                          onChange={(e) => setAuthData({...authData, email: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          value={authData.password}
                          onChange={(e) => setAuthData({...authData, password: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                          required
                        />
                      </div>

                      <Button
                        onClick={handleLogin}
                        disabled={isLoading}
                        className="w-full"
                      >
                        {isLoading ? t('booking.processing') : t('booking.login')}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Registration Form */}
                {authMode === 'register' && !isAuthenticated && (
                  <div className="max-w-md mx-auto">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                            First Name
                          </label>
                          <input
                            type="text"
                            value={authData.firstName}
                            onChange={(e) => setAuthData({...authData, firstName: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                            Last Name
                          </label>
                          <input
                            type="text"
                            value={authData.lastName}
                            onChange={(e) => setAuthData({...authData, lastName: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={authData.email}
                          onChange={(e) => setAuthData({...authData, email: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={authData.phone}
                          onChange={(e) => setAuthData({...authData, phone: e.target.value})}
                          placeholder="+212 6XX XXX XXX"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                          Password
                        </label>
                        <input
                          type="password"
                          value={authData.password}
                          onChange={(e) => setAuthData({...authData, password: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-sofia font-medium text-text-primary mb-2">
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          value={authData.confirmPassword}
                          onChange={(e) => setAuthData({...authData, confirmPassword: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-300 font-sofia"
                          required
                        />
                      </div>

                      <Button
                        onClick={handleRegister}
                        disabled={isLoading}
                        className="w-full"
                      >
                        {isLoading ? t('booking.processing') : t('booking.register')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Confirmation */}
            {currentStep === 5 && (
              <div>
                <h2 className="text-2xl font-sofia font-bold text-black mb-6">Booking Confirmation</h2>

                <div className="space-y-6">
                  {/* Villa Details */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-sofia font-semibold text-black mb-4">Villa Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-text-secondary font-sofia">Villa:</span>
                        <span className="font-sofia font-medium">
                          {villasWithLabels.find(v => v.id === bookingData.villa)?.name}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-text-secondary font-sofia">Guests:</span>
                        <p className="font-sofia font-medium">
                          {bookingData.guests.adults} {t('booking.guestBreakdown.adultsShort', 'adults')}
                          {bookingData.guests.children > 0 && `, ${bookingData.guests.children} ${t('booking.guestBreakdown.childrenShort', 'children')}`}
                          {bookingData.guests.babies > 0 && `, ${bookingData.guests.babies} ${t('booking.guestBreakdown.babiesShort', 'babies')}`}
                          {bookingData.guests.pets > 0 && `, ${bookingData.guests.pets} ${t('booking.guestBreakdown.petsShort', 'pets')}`}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-text-secondary font-sofia">Check-in:</span>
                        <p className="font-sofia font-medium">{bookingData.checkIn}</p>
                      </div>
                      <div>
                        <span className="text-sm text-text-secondary font-sofia">Check-out:</span>
                        <p className="font-sofia font-medium">{bookingData.checkOut}</p>
                      </div>
                    </div>
                  </div>

                  {/* Guest Details */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-sofia font-semibold text-black mb-4">Guest Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-text-secondary font-sofia">Name:</span>
                        <p className="font-sofia font-medium">
                          {isAuthenticated
                            ? `${user?.first_name} ${user?.last_name}`
                            : `${bookingData.personalInfo.firstName} ${bookingData.personalInfo.lastName}`
                          }
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-text-secondary font-sofia">Email:</span>
                        <p className="font-sofia font-medium">
                          {isAuthenticated ? user?.email : bookingData.personalInfo.email}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-text-secondary font-sofia">Phone:</span>
                        <p className="font-sofia font-medium">
                          {isAuthenticated ? user?.phone : bookingData.personalInfo.phone}
                        </p>
                      </div>
                      {bookingData.personalInfo.specialRequests && (
                        <div className="md:col-span-2">
                          <span className="text-sm text-text-secondary font-sofia">Special Requests:</span>
                          <p className="font-sofia font-medium">{bookingData.personalInfo.specialRequests}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Services */}
                  {bookingData.services.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-sofia font-semibold text-black mb-4">Additional Services</h3>
                      <div className="space-y-2">
                        {bookingData.services.map(serviceId => {
                          const service = additionalServices.find(s => s.id === serviceId);
                          return (
                            <div key={serviceId} className="flex justify-between">
                              <span className="font-sofia">{service.name}</span>
                              <span className="font-sofia font-medium">{formatMad(service.price)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Price Breakdown */}
                  <div className="bg-accent/10 rounded-lg p-6">
                    <h3 className="text-lg font-sofia font-semibold text-black mb-4">Price Breakdown</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-sofia">
                          {t('booking.basePrice', 'Accommodation')} ({Math.ceil((new Date(bookingData.checkOut) - new Date(bookingData.checkIn)) / (1000 * 60 * 60 * 24))} {t('booking.nightsLabel', 'nights')}):
                        </span>
                        <span className="font-sofia font-medium">
                          {quote?.total_price != null ? formatMad(quote.total_price) : '—'}
                        </span>
                      </div>
                      {bookingData.services.length > 0 && (
                        <div className="flex justify-between">
                          <span className="font-sofia">Additional Services:</span>
                          <span className="font-sofia font-medium">{formatMad(calculateServicesTotal())}</span>
                        </div>
                      )}
                      <div className="border-t border-accent/20 pt-2 flex justify-between text-lg">
                        <span className="font-sofia font-bold text-black">Total Amount:</span>
                        <span className="font-sofia font-bold text-[#81736d]">
                          {formatMad(totalPrice)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </Card>

            {/* Navigation — normal flow (above footer on mobile) */}
            <div className="flex justify-between gap-3 mt-8 pt-6 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className={`flex-1 lg:flex-none ${currentStep === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {t('booking.previous')}
              </Button>

              {currentStep < 5 ? (
                <Button
                  onClick={handleNext}
                  disabled={
                    (currentStep === 1 && !bookingData.villa) ||
                    (currentStep === 2 && (!bookingData.checkIn || !bookingData.checkOut || quoteLoading || !quote?.available || !guestsValid)) ||
                    (currentStep === 4 && authMode !== 'guest' && !isAuthenticated) ||
                    (currentStep === 4 && (authMode === 'guest' || isAuthenticated) && (
                      !getPersonalInfoValue('firstName') ||
                      !getPersonalInfoValue('lastName') ||
                      !getPersonalInfoValue('email') ||
                      !getPersonalInfoValue('phone')
                    ))
                  }
                  className="flex-1 lg:flex-none"
                >
                  {t('booking.next')}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex-1 lg:flex-none bg-green-600 hover:bg-green-700"
                >
                  {isLoading ? 'Processing...' : 'Confirm Booking'}
                </Button>
              )}
            </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingScreen;
